import { cancellableDelay, globalUtils } from '@gat-ship/utils'
import { tryOnUnmounted } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useUserApiCalls } from '@gat-ship/lib/http-client'

const CACHE_COOLDOWN_KEY = 'gatship_cache_invalidation_cooldown'
const COOLDOWN_DURATION_MS = 60 * 1000 // 1 minute

export function useInvalidateCache() {
  const { invalidateCacheRequest } = useUserApiCalls()
  const lastInvalidationTime = ref<number | null>(null)
  const isCooldownActive = ref(false)
  const currentTime = ref(Date.now())
  let countdownTimer: ReturnType<typeof setTimeout> | null = null

  // Initialize from localStorage on mount
  function initializeCooldown() {
    try {
      const stored = localStorage.getItem(CACHE_COOLDOWN_KEY)
      if (stored) {
        const timestamp = parseInt(stored, 10)
        const now = Date.now()

        if (now - timestamp < COOLDOWN_DURATION_MS) {
          // Cooldown is still active
          lastInvalidationTime.value = timestamp
          isCooldownActive.value = true

          // Set up timer to clear cooldown when it expires
          const remainingTime = COOLDOWN_DURATION_MS - (now - timestamp)
          setTimeout(() => {
            isCooldownActive.value = false
            lastInvalidationTime.value = null
            localStorage.removeItem(CACHE_COOLDOWN_KEY)
          }, remainingTime)

          // Start countdown immediately
          startCountdown()
        } else {
          // Cooldown has expired, clean up
          localStorage.removeItem(CACHE_COOLDOWN_KEY)
        }
      }
    } catch (error) {
      console.warn(
        'Failed to initialize cache cooldown from localStorage:',
        error
      )
    }
  }

  // Start cooldown
  function startCooldown() {
    const now = Date.now()
    lastInvalidationTime.value = now
    isCooldownActive.value = true

    try {
      localStorage.setItem(CACHE_COOLDOWN_KEY, now.toString())
    } catch (error) {
      console.warn('Failed to save cache cooldown to localStorage:', error)
    }

    // Start countdown immediately
    startCountdown()

    // Set up timer to clear cooldown after 1 minute
    setTimeout(() => {
      isCooldownActive.value = false
      lastInvalidationTime.value = null
      try {
        localStorage.removeItem(CACHE_COOLDOWN_KEY)
      } catch (error) {
        console.warn(
          'Failed to remove cache cooldown from localStorage:',
          error
        )
      }
    }, COOLDOWN_DURATION_MS)
  }

  // Calculate remaining cooldown time in seconds
  const remainingCooldownSeconds = computed(() => {
    if (!lastInvalidationTime.value || !isCooldownActive.value) {
      return 0
    }

    const elapsed = currentTime.value - lastInvalidationTime.value
    const remaining = Math.max(0, COOLDOWN_DURATION_MS - elapsed)
    return Math.ceil(remaining / 1000)
  })

  // Start real-time countdown when cooldown is active
  function startCountdown() {
    if (countdownTimer) {
      clearInterval(countdownTimer)
    }

    countdownTimer = setInterval(() => {
      if (!isCooldownActive.value) {
        if (countdownTimer) {
          clearInterval(countdownTimer)
          countdownTimer = null
        }
        return
      }

      // Update current time to trigger reactivity
      currentTime.value = Date.now()
    }, 1000)
  }

  // Clean up timer on unmount
  tryOnUnmounted(() => {
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  })

  async function _invalidateCache() {
    const commandId = globalUtils.functions.uuid()
    const result = await invalidateCacheRequest(commandId)
    return result
  }

  // Create an abort controller for the current cache invalidation operation
  let currentCacheInvalidationController: AbortController | null = null

  // Wrapped invalidateCache function with cooldown
  async function invalidateCache(options?: {
    /** Whether to reload the page after invalidating the cache. @default true */
    reloadPage?: boolean
    /** Whether to force the cache invalidation. @default false */
    force?: boolean
    /** The delay in milliseconds before the cache invalidation. @default 2000 */
    delay?: number
  }) {
    if (isCooldownActive.value && !options?.force) {
      throw new Error(
        `Cache invalidation is on cooldown. Please wait ${remainingCooldownSeconds.value} seconds.`
      )
    }

    try {
      if (options?.delay) {
        // Abort any previous cache invalidation operation
        if (currentCacheInvalidationController) {
          currentCacheInvalidationController.abort()
        }

        // Create a new abort controller for this operation
        currentCacheInvalidationController = new AbortController()
        await cancellableDelay(
          options.delay,
          currentCacheInvalidationController.signal
        )
      }
      const result = await _invalidateCache()
      if (!options?.force) {
        startCooldown()
      }
      if (options?.reloadPage ?? true) {
        window.location.reload()
      }
      return result
    } catch (error) {
      if (error instanceof Error && error.message === 'Operation aborted') {
        // Operation was aborted, do nothing
        return
      }
      throw error
    } finally {
      if (currentCacheInvalidationController) {
        currentCacheInvalidationController = null
      }
    }
  }

  return {
    isCooldownActive,
    remainingCooldownSeconds,
    initializeCooldown,
    startCooldown,
    startCountdown,
    invalidateCache
  }
}
